import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpEvent,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AutomaticRecoveryCaseConfigModel } from '../models/automaticRecoveryCaseConfigModel';
import { CustomHttpUrlEncodingCodec } from '../../services/encoder';
import { GetAutomaticRecoveryCaseConfig } from '../models/getAutomaticRecoveryCaseConfig';

@Injectable()
export class AutomaticRecoveryCaseConfigService {
  private readonly apiUrl = `${environment.ccdcrp_api_base_url}/automatic-recovery-case-config`;
  public defaultHeaders = new HttpHeaders();

  constructor(protected httpClient: HttpClient) {}

  /**
   * @param request The query parameters used to filter the configurations.
   * @param observe Set whether to return the data Observable as the body, response, or events. Defaults to returning the body.
   * @param reportProgress Flag to report request and response progress.
   * @returns An Observable containing the list of configurations.
   */
  Get(
    request: GetAutomaticRecoveryCaseConfig,
    aPIVersion?: string,
    observe: any = 'body',
    reportProgress: boolean = false
  ): Observable<any> {
    let headers = this.defaultHeaders;

    let queryParameters = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });
    if (
      request.paginationPageNumber !== undefined &&
      request.paginationPageNumber !== null
    ) {
      queryParameters = queryParameters.set(
        'Pagination.PageNumber',
        <any>request.paginationPageNumber
      );
    }
    if (
      request.paginationPageSize !== undefined &&
      request.paginationPageSize !== null
    ) {
      queryParameters = queryParameters.set(
        'Pagination.PageSize',
        <any>request.paginationPageSize
      );
    }
    if (
      request.paginationSortOrder !== undefined &&
      request.paginationSortOrder !== null
    ) {
      queryParameters = queryParameters.set(
        'Pagination.SortOrder',
        <any>request.paginationSortOrder
      );
    }
    if (
      request.paginationSortColumn !== undefined &&
      request.paginationSortColumn !== null
    ) {
      queryParameters = queryParameters.set(
        'Pagination.SortColumn',
        <any>request.paginationSortColumn
      );
    }
    if (request.recoveryTypes) {
      request.recoveryTypes.forEach((element) => {
        queryParameters = queryParameters.append('RecoveryTypes', <any>element);
      });
    }
    if (request.countryCodes) {
      request.countryCodes.forEach((element) => {
        queryParameters = queryParameters.append('CountryCodes', <any>element);
      });
    }
    if (request.clusterCodes) {
      request.clusterCodes.forEach((element) => {
        queryParameters = queryParameters.append('ClusterCodes', <any>element);
      });
    }
    if (request.regionCodes) {
      request.regionCodes.forEach((element) => {
        queryParameters = queryParameters.append('RegionCodes', <any>element);
      });
    }
    if (request.countryNames) {
      request.countryNames.forEach((element) => {
        queryParameters = queryParameters.append('CountryNames', <any>element);
      });
    }
    if (request.clusterNames) {
      request.clusterNames.forEach((element) => {
        queryParameters = queryParameters.append('ClusterNames', <any>element);
      });
    }
    if (request.regionNames) {
      request.regionNames.forEach((element) => {
        queryParameters = queryParameters.append('RegionNames', <any>element);
      });
    }

    if (aPIVersion !== undefined && aPIVersion !== null) {
      headers = headers.set('API-Version', String(aPIVersion));
    }

    // to determine the Accept header
    const httpHeaderAccepts: string[] = [
      'text/plain',
      'application/json',
      'text/json',
    ];
    headers = headers.set('Accept', httpHeaderAccepts);

    // to determine the Content-Type header

    return this.httpClient.request<AutomaticRecoveryCaseConfigModel[]>(
      'get',
      `${this.apiUrl}`,
      {
        params: queryParameters,
        headers: headers,
        observe: observe,
        reportProgress: reportProgress,
      }
    );
  }

  /**
   * @param request The query parameters used to filter the configurations.
   * @param observe Set whether to return the data Observable as the body, response, or events. Defaults to returning the body.
   * @param reportProgress Flag to report request and response progress.
   * @returns An Observable containing the count of configurations.
   */
  GetCount(
    request: GetAutomaticRecoveryCaseConfig,
    aPIVersion?: string,
    observe: any = 'body',
    reportProgress: boolean = false
  ): Observable<any> {
    let headers = this.defaultHeaders;

    let queryParameters = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });
    if (
      request.paginationPageNumber !== undefined &&
      request.paginationPageNumber !== null
    ) {
      queryParameters = queryParameters.set(
        'Pagination.PageNumber',
        <any>request.paginationPageNumber
      );
    }
    if (
      request.paginationPageSize !== undefined &&
      request.paginationPageSize !== null
    ) {
      queryParameters = queryParameters.set(
        'Pagination.PageSize',
        <any>request.paginationPageSize
      );
    }
    if (
      request.paginationSortOrder !== undefined &&
      request.paginationSortOrder !== null
    ) {
      queryParameters = queryParameters.set(
        'Pagination.SortOrder',
        <any>request.paginationSortOrder
      );
    }
    if (
      request.paginationSortColumn !== undefined &&
      request.paginationSortColumn !== null
    ) {
      queryParameters = queryParameters.set(
        'Pagination.SortColumn',
        <any>request.paginationSortColumn
      );
    }
    if (request.recoveryTypes) {
      request.recoveryTypes.forEach((element) => {
        queryParameters = queryParameters.append('RecoveryTypes', <any>element);
      });
    }
    if (request.countryCodes) {
      request.countryCodes.forEach((element) => {
        queryParameters = queryParameters.append('CountryCodes', <any>element);
      });
    }
    if (request.clusterCodes) {
      request.clusterCodes.forEach((element) => {
        queryParameters = queryParameters.append('ClusterCodes', <any>element);
      });
    }
    if (request.regionCodes) {
      request.regionCodes.forEach((element) => {
        queryParameters = queryParameters.append('RegionCodes', <any>element);
      });
    }
    if (request.countryNames) {
      request.countryNames.forEach((element) => {
        queryParameters = queryParameters.append('CountryNames', <any>element);
      });
    }
    if (request.clusterNames) {
      request.clusterNames.forEach((element) => {
        queryParameters = queryParameters.append('ClusterNames', <any>element);
      });
    }
    if (request.regionNames) {
      request.regionNames.forEach((element) => {
        queryParameters = queryParameters.append('RegionNames', <any>element);
      });
    }

    if (aPIVersion !== undefined && aPIVersion !== null) {
      headers = headers.set('API-Version', String(aPIVersion));
    }

    // to determine the Accept header
    const httpHeaderAccepts: string[] = [
      'text/plain',
      'application/json',
      'text/json',
    ];
    headers = headers.set('Accept', httpHeaderAccepts);

    // to determine the Content-Type header

    return this.httpClient.request<number>('get', `${this.apiUrl}/count`, {
      params: queryParameters,
      headers: headers,
      observe: observe,
      reportProgress: reportProgress,
    });
  }

  /**
   * Adds a new automatic recovery case configuration.
   * @param body The configuration model to add.
   * @returns Observable of the added AutomaticRecoveryCaseConfigModel.
   */
  Add(
    body: AutomaticRecoveryCaseConfigModel,
    aPIVersion?: string,
    observe?: 'body',
    reportProgress?: boolean
  ): Observable<AutomaticRecoveryCaseConfigModel> {
    let headers = this.defaultHeaders;
    if (aPIVersion !== undefined && aPIVersion !== null) {
      headers = headers.set('API-Version', String(aPIVersion));
    }

    // to determine the Accept header
    const httpHeaderAccepts: string[] = [
      'text/plain',
      'application/json',
      'text/json',
    ];
    headers = headers.set('Accept', httpHeaderAccepts);

    // to determine the Content-Type header
    const consumes: string[] = [
      'application/json',
      'text/json',
      'application/_*+json',
    ];
    headers = headers.set('Content-Type', consumes);

    return this.httpClient.request<AutomaticRecoveryCaseConfigModel>(
      'post',
      `${this.apiUrl}`,
      {
        body: body,
        headers: headers,
        observe: observe,
        reportProgress: reportProgress,
      }
    );
  }

  /**
   * Updates an existing automatic recovery case configuration.
   * @param body The configuration model to update.
   * @returns Observable of the updated AutomaticRecoveryCaseConfigModel.
   */
  Update(
    body: AutomaticRecoveryCaseConfigModel,
    aPIVersion?: string,
    observe?: 'body',
    reportProgress?: boolean
  ): Observable<AutomaticRecoveryCaseConfigModel> {
    if (body.id === null || body.id === undefined) {
      throw new Error(
        'Required parameter id was null or undefined when calling Update.'
      );
    }

    let headers = this.defaultHeaders;
    if (aPIVersion !== undefined && aPIVersion !== null) {
      headers = headers.set('API-Version', String(aPIVersion));
    }

    // to determine the Accept header
    const httpHeaderAccepts: string[] = [
      'text/plain',
      'application/json',
      'text/json',
    ];
    headers = headers.set('Accept', httpHeaderAccepts);

    // to determine the Content-Type header
    const consumes: string[] = [
      'application/json',
      'text/json',
      'application/_*+json',
    ];
    headers = headers.set('Content-Type', consumes);

    return this.httpClient.request<AutomaticRecoveryCaseConfigModel>(
      'put',
      `${this.apiUrl}`,
      {
        body: body,
        headers: headers,
        observe: observe,
        reportProgress: reportProgress,
      }
    );
  }

  /**
   * Retrieves an automatic recovery case configuration by its unique identifier.
   * @param id The unique identifier of the configuration to retrieve.
   * @returns Observable of the AutomaticRecoveryCaseConfigModel.
   */
  GetById(
    id: number,
    aPIVersion?: string,
    reportProgress: boolean = false
  ): Observable<AutomaticRecoveryCaseConfigModel> {
    if (id === null || id === undefined) {
      throw new Error(
        'Required parameter id was null or undefined when calling GetById.'
      );
    }

    let headers = this.defaultHeaders;
    if (aPIVersion !== undefined && aPIVersion !== null) {
      headers = headers.set('API-Version', String(aPIVersion));
    }

    // to determine the Accept header
    const httpHeaderAccepts: string[] = [
      'text/plain',
      'application/json',
      'text/json',
    ];
    headers = headers.set('Accept', httpHeaderAccepts);

    return this.httpClient.request<AutomaticRecoveryCaseConfigModel>(
      'get',
      `${this.apiUrl}/${encodeURIComponent(String(id))}`,
      {
        headers: headers,
        observe: 'body',
        reportProgress: reportProgress,
      }
    );
  }

  /**
   * Deletes an automatic recovery case configuration by ID.
   * @param id The ID of the configuration to delete.
   * @returns Observable of a boolean indicating success.
   */
  Delete(
    id: number,
    aPIVersion?: string,
    observe: any = 'body',
    reportProgress: boolean = false
  ): Observable<HttpEvent<boolean>> {
    if (id === null || id === undefined) {
      throw new Error(
        'Required parameter id was null or undefined when calling Delete.'
      );
    }

    let headers = this.defaultHeaders;
    if (aPIVersion !== undefined && aPIVersion !== null) {
      headers = headers.set('API-Version', String(aPIVersion));
    }

    // to determine the Accept header
    const httpHeaderAccepts: string[] = [
      'text/plain',
      'application/json',
      'text/json',
    ];
    headers = headers.set('Accept', httpHeaderAccepts);

    // to determine the Content-Type header

    return this.httpClient.request<boolean>(
      'delete',
      `${this.apiUrl}/${encodeURIComponent(String(id))}`,
      {
        headers: headers,
        observe: observe,
        reportProgress: reportProgress,
      }
    );
  }
}
